/**
 * Catálogos en memoria.
 * Se cargan una vez al abrir la app y quedan disponibles para los
 * autocompletados del formulario de captura, que debe responder al instante
 * incluso sin señal.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { COL } from '@/db/firebase';
import { listar, modoLocal, suscribir } from '@/db/repositorio';
import { sembrar, yaSembrado } from '@/db/semilla';
import type {
  DocCapitulo,
  DocCatalogo,
  DocCentroCosto,
  DocItemContrato,
  DocPlanta,
  DocProducto,
} from '@/db/tiposDatos';
import { precioVigente } from '@/domain/precios';
import { useAuth } from './useAuth';

export interface Catalogos {
  plantas: DocPlanta[];
  clientes: DocCatalogo[];
  productos: DocProducto[];
  equipos: DocCatalogo[];
  personas: DocCatalogo[];
  centrosCosto: DocCentroCosto[];
  destinos: DocCatalogo[];
  capitulos: DocCapitulo[];
  items: (DocItemContrato & { precioUf: number })[];
}

const VACIO: Catalogos = {
  plantas: [],
  clientes: [],
  productos: [],
  equipos: [],
  personas: [],
  centrosCosto: [],
  destinos: [],
  capitulos: [],
  items: [],
};

interface Contexto {
  catalogos: Catalogos;
  cargando: boolean;
  error: string | null;
  recargar: () => Promise<void>;
  /** Busca una partida del contrato por código. */
  buscarItem: (codigo: string) => (DocItemContrato & { precioUf: number }) | null;
}

const Ctx = createContext<Contexto>({
  catalogos: VACIO,
  cargando: true,
  error: null,
  recargar: async () => {},
  buscarItem: () => null,
});

export function ProveedorCatalogos({ children }: { children: ReactNode }) {
  // Sin sesión no se lee nada: las reglas de Firestore lo rechazarían y la
  // carga quedaría colgada. Se espera a que la autenticación resuelva.
  const { dentro, cargando: autenticando } = useAuth();

  const [catalogos, setCatalogos] = useState<Catalogos>(VACIO);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      setError(null);
      if (!(await yaSembrado())) await sembrar(null);

      const [
        plantas,
        clientes,
        productos,
        equipos,
        personas,
        centrosCosto,
        destinos,
        capitulos,
        items,
      ] = await Promise.all([
        listar<DocPlanta>(COL.plantas),
        listar<DocCatalogo>(COL.clientes),
        listar<DocProducto>(COL.productos),
        listar<DocCatalogo>(COL.equipos),
        listar<DocCatalogo>(COL.personas),
        listar<DocCentroCosto>(COL.centrosCosto),
        listar<DocCatalogo>(COL.destinos),
        listar<DocCapitulo>(COL.capitulos),
        listar<DocItemContrato>(COL.itemsContrato),
      ]);

      const ordenar = <T extends { nombre?: string; codigo: string }>(xs: T[]) =>
        xs.sort((a, b) => (a.nombre ?? a.codigo).localeCompare(b.nombre ?? b.codigo, 'es'));

      setCatalogos({
        plantas: plantas.sort((a, b) => a.codigo.localeCompare(b.codigo)),
        clientes: ordenar(clientes),
        productos: ordenar(productos),
        equipos: ordenar(equipos),
        personas: ordenar(personas),
        centrosCosto: ordenar(centrosCosto),
        destinos: ordenar(destinos),
        capitulos: capitulos.sort((a, b) => a.orden - b.orden),
        items: items
          .map((i) => ({ ...i, precioUf: precioVigente(i) }))
          .sort((a, b) =>
            a.codigo.localeCompare(b.codigo, 'es', { numeric: true, sensitivity: 'base' }),
          ),
      });
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los catálogos.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (autenticando) return;
    if (!dentro) {
      // Al cerrar sesión se descartan los catálogos: no deben quedar a la
      // vista del siguiente que entre.
      setCatalogos(VACIO);
      setCargando(false);
      return;
    }
    setCargando(true);
    void cargar();
  }, [cargar, dentro, autenticando]);

  // En Firestore, cualquier cambio de catálogo desde otro dispositivo se refleja aquí.
  useEffect(() => {
    if (modoLocal || !dentro) return;
    const off = [COL.productos, COL.clientes, COL.itemsContrato].map((c) =>
      suscribir(c, () => void cargar()),
    );
    return () => off.forEach((fn) => fn());
  }, [cargar, dentro]);

  const indiceItems = useMemo(
    () => new Map(catalogos.items.map((i) => [i.codigo, i])),
    [catalogos.items],
  );

  const buscarItem = useCallback(
    (codigo: string) => indiceItems.get(codigo) ?? null,
    [indiceItems],
  );

  return (
    <Ctx.Provider value={{ catalogos, cargando, error, recargar: cargar, buscarItem }}>
      {children}
    </Ctx.Provider>
  );
}

export function useCatalogos() {
  return useContext(Ctx);
}
